import { Navigate } from 'react-router-dom';
import DashboardLayout from '../../../layouts/dashboard/DashboardLayout';
import Dashboard from '../view/Dashboard';
// import Customers from '../../customers/view/Customers';
import Categories from '../../categories/view/Categories';
import Staff from '../../users/view/Users';
import Branches from '../../branches/view/Branches';
import UserAccount from '../../auth/view/UserAccount';
import SimpleLayout from '../../../layouts/simple/SimpleLayout';
import Page404 from '../../../pages/Page404';
import AuthGuard from '../../../shared/auth/authGuards';
import AuditView from '../../audit/view/audit-view';
import AuditForm from '../../audit/view/new-edit-audit-form';
import AuditDetailView from '../../audit/view/audit-detail-view';
import Reports from '../../reports/view/Reports';
import FixedAssets from '../../fixedassets/view/FixedAssets';
import { AssetDetail, AssetTransfers, CheckoutAssets, NewAssetForm } from '../../fixedassets/components';
import { NewUserForm } from '../../users/components';
import OnsiteMaintenance from '../../maintenance/view/OnsiteMaintenance';
import OffsiteMaintenance from '../../maintenance/view/Maintenance';
import { MaintenanceDetail } from '../../maintenance/components';
import UserDashboard from '../view/UserDashboard';
import BranchDashboard from '../view/BranchDashboard';
import ServiceProviderMaintenance from '../../maintenance/view/ServiceProviderMaintenance';
import Handovers from '../../handovers/view/Handovers';
import Disposals from '../../disposals/view/Disposals';
import Invoices from '../../payments/view/Invoices';
import { getProfile } from '../../../shared/auth/authService';
import { ROLES } from '../../../shared/auth/roles';
import Inbox from '../../workflow/view/Inbox';
import RequestDetail from '../../workflow/view/RequestDetail';
import Notifications from '../../notifications/view/Notifications';

// Every role gets the inbox, a request's history and the notification feed.
const workflowPages = [
  { path: 'inbox', element: <Inbox /> },
  { path: 'requests/:key', element: <RequestDetail /> },
  { path: 'notifications', element: <Notifications /> },
];

// Was ['MANAGER', 'BRANCH_MANAGER']; BRANCH_MANAGER is not a role the API
// issues, so it never matched and this was already MANAGER-only in practice.
const allowedRoles = [ROLES.MANAGER];

/**
 * Build the dashboard route tree for the signed-in user.
 *
 * This was a module-level constant derived from localStorage at import time, so
 * the route tree reflected whoever was signed in when the bundle first loaded.
 * Signing in as a different role kept the previous user's routes until a full
 * page reload. Router() calls this during render instead.
 */
export const getDashboardRoutes = () => {
  const storedUser = getProfile() || {};
  const isManager = storedUser?.role && allowedRoles.includes(storedUser.role);

  return (isManager && [
  {
    path: '/dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { element: <Navigate to="/dashboard/app" />, index: true },
      { path: 'app', element: <BranchDashboard /> },
      ...workflowPages,
      { path: 'assets', element: <FixedAssets /> },
      { path: 'transfers', element: <AssetTransfers /> },
      { path: 'transfers/:id', element: <CheckoutAssets /> },
      { path: 'assets/:id', element: <AssetDetail /> },
      { path: 'categories', element: <Categories /> },
      { path: 'maintenance', element: <ServiceProviderMaintenance /> },
      { path: 'maintenance/:id', element: <MaintenanceDetail /> },
      { path: 'handovers', element: <Handovers /> },
      { path: 'disposals', element: <Disposals /> },
      { path: 'settings', element: <UserAccount /> },
    ],
  },
  {
    element: <SimpleLayout />,
    children: [
      { element: <Navigate to="/dashboard/app" />, index: true },
      { path: '404', element: <Page404 /> },
      { path: '*', element: <Navigate to="/404" /> },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
]) ||
  (storedUser?.role === 'SERVICE_PROVIDER' && [
    {
      path: '/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/app" />, index: true },
        { path: 'app', element: <UserDashboard /> },
        ...workflowPages,
        { path: 'maintenance', element: <ServiceProviderMaintenance /> },
        { path: 'maintenance/:id', element: <MaintenanceDetail /> },
        { path: 'settings', element: <UserAccount /> },
      ],
    },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/dashboard/app" />, index: true },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/dashboard" replace />,
    },
  ]) || [
    {
      path: '/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/app" />, index: true },
        { path: 'app', element: <Dashboard /> },
        ...workflowPages,
        { path: 'assets', element: <FixedAssets /> },
        { path: 'transfers', element: <AssetTransfers /> },
        { path: 'transfers/:id', element: <CheckoutAssets /> },
        { path: 'assets/new', element: <NewAssetForm /> },
        { path: 'assets/:id/edit', element: <NewAssetForm /> },
        { path: 'assets/:id', element: <AssetDetail /> },
        { path: 'branches', element: <Branches /> },
        { path: 'categories', element: <Categories /> },
        { path: 'users', element: <Staff /> },
        { path: 'users/new', element: <NewUserForm /> },
        { path: 'audits', element: <AuditView /> },
        { path: 'audits/new', element: <AuditForm /> },
        { path: 'audits/:id', element: <AuditDetailView /> },
        { path: 'maintenance-onsite', element: <OnsiteMaintenance /> },
        { path: 'maintenance-offsite', element: <OffsiteMaintenance /> },
        { path: 'invoices', element: <Invoices /> },
        { path: 'maintenance-info/:id', element: <MaintenanceDetail /> },
        { path: 'handovers', element: <Handovers /> },
        { path: 'disposals', element: <Disposals /> },
        { path: 'reports', element: <Reports /> },
        { path: 'settings', element: <UserAccount /> },
      ],
    },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/dashboard/app" />, index: true },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/dashboard" replace />,
    },
  ];
};
